import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useStageFormError } from '../hooks/use-stage-form-error';
import { useSkipStage } from '../hooks/use-workflow-mutations';
import { skipStageSchema, type SkipStageForm } from '../schemas/stage-forms';

import { ActionDialog } from './action-dialog';
import type { StageTarget } from './stage-target';

const KNOWN = new Set(['reason']);

export function SkipStageDialog({
  target,
  onOpenChange,
}: {
  target: StageTarget;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('production');
  const skip = useSkipStage(target.storeId, target.orderId);
  const form = useForm<SkipStageForm>({
    resolver: useI18nResolver(skipStageSchema),
    defaultValues: { reason: '' },
  });
  const onError = useStageFormError(form.setError, { knownFields: KNOWN });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await skip.mutateAsync({ key: target.key, body: { reason: values.reason } });
      onOpenChange(false);
    } catch (error) {
      onError(error);
    }
  });

  return (
    <Form {...form}>
      <ActionDialog
        open
        onOpenChange={onOpenChange}
        title={t('skip.title', { name: target.name })}
        description={t('skip.description')}
        rootError={form.formState.errors.root?.server?.message}
        isPending={skip.isPending}
        submitLabel={t('skip.submit')}
        submittingLabel={t('skip.submitting')}
        onSubmit={submit}
      >
        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('skip.reason')}</FormLabel>
              <FormControl>
                <Textarea {...field} rows={3} maxLength={500} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </ActionDialog>
    </Form>
  );
}
